/**
 * Jain Gaurav Yatra — Certificate Template (singleton settings).
 * Backend: /jain_gaurav_yatra/certificate-template/  (cookie-session auth + X-Tenant-ID, JSON).
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";
import type { CertSettings } from "@/lib/certificate";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

function jsonHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    "Content-Type": "application/json",
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg = pick(d?.message) ?? pick(d?.detail);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const TEMPLATE_PATH = "/jain_gaurav_yatra/certificate-template/";

const KEYS: (keyof CertSettings)[] = [
  "title_en",
  "title_hi",
  "title_gu",
  "message_en",
  "message_hi",
  "message_gu",
  "signatory_name",
  "signatory_title_en",
  "signatory_title_hi",
  "signatory_title_gu",
];

function toSettings(row: Record<string, unknown>): CertSettings {
  const out = {} as CertSettings;
  for (const k of KEYS) {
    const v = row[k];
    out[k] = v === null || v === undefined ? "" : String(v);
  }
  return out;
}

export async function getCertificateTemplate(): Promise<CertSettings> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${TEMPLATE_PATH}`, {
    method: "GET",
    headers: jsonHeaders(),
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toSettings((data ?? {}) as Record<string, unknown>);
}

export async function saveCertificateTemplate(payload: CertSettings): Promise<CertSettings> {
  const base = apiBase();
  const res = await fetch(`${base}${TEMPLATE_PATH}`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "include",
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toSettings((data ?? {}) as Record<string, unknown>);
}
